// components/chat/chat-input/LexicalEditor.tsx
import React, { useCallback, forwardRef, useImperativeHandle, useRef } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, $isRangeSelection, $createTextNode, $getRoot } from "lexical";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { lexicalConfig } from "./LexicalComposerConfig";
import { LexicalPlaceholder } from "./LexicalPlaceholder";
import { LexicalSendOnEnter } from "./LexicalSendOnEnter";
import { LexicalClearPlugin } from "./LexicalClearPlugin";
import { LexicalSendCommand } from "./LexicalSendCommand";
import { MentionsPlugin } from "./MentionsPlugin";
import { $createMentionNode } from "./MentionNode";
import type { ContextItem } from "@/components/shared/AddContextMenu";

export interface LexicalEditorHandle {
  insertMention: (item: ContextItem) => void;
  insertTextAtCursor: (text: string) => void;
}

interface LexicalEditorWrapperProps {
  disabled?: boolean;
  onSend: (text: string) => void;
  clearTrigger?: number;
  sendTrigger?: number;
  placeholder?: string;
  onMentionSelect?: (toolId: string) => void;
  onContextSelect?: (item: ContextItem) => void;
  onOpenContextMenu?: () => void;
  onTextChange?: (text: string) => void;
}

// Inner component that has access to the Lexical editor context
const LexicalEditorInner = forwardRef<LexicalEditorHandle, LexicalEditorWrapperProps>(({
  disabled,
  onSend,
  clearTrigger = 0,
  sendTrigger = 0,
  placeholder = "Learn anything",
  onMentionSelect,
  onContextSelect,
  onOpenContextMenu,
  onTextChange,
}, ref) => {
  const [editor] = useLexicalComposerContext();
  const previousTextRef = useRef("");

  const handleEditorChange = useCallback(() => {
    editor.getEditorState().read(() => {
      const text = $getRoot().getTextContent();
      if (text !== previousTextRef.current) {
        previousTextRef.current = text;
        onTextChange?.(text);
      }
    });
  }, [editor, onTextChange]);

  // Insert mention when item is selected from the menu
  const insertMention = useCallback((item: ContextItem) => {
    editor.update(() => {
      // Find the @ character and remove it
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const text = anchorNode.getTextContent();
        
        // If text ends with @, we need to remove it
        if (text.endsWith("@")) {
          const anchorOffset = selection.anchor.offset;
          // Move selection back to include the @
          selection.anchor.offset = Math.max(0, anchorOffset - 1);
          selection.focus.offset = anchorOffset;
          selection.removeText();
        }
        
        // Insert the mention node with iconColor for proper styling
        const mentionNode = $createMentionNode(item.id, item.label, item.iconColor);
        const spaceNode = $createTextNode(" ");
        selection.insertNodes([mentionNode, spaceNode]);
        spaceNode.select();
      }
    });

    // Notify parent about selection if callback provided
    onMentionSelect?.(item.id);
    onContextSelect?.(item);
  }, [editor, onMentionSelect, onContextSelect]);

  // Insert plain text at cursor position
  const insertTextAtCursor = useCallback((text: string) => {
    editor.focus(() => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertNodes([$createTextNode(text)]);
        } else {
          // Fallback: insert at end
          const root = editor.getRootElement();
          if (root) {
            const textNode = $createTextNode(text);
            const sel = $getSelection();
            if ($isRangeSelection(sel)) {
              sel.insertNodes([textNode]);
            }
          }
        }
      });
    });
  }, [editor]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    insertMention,
    insertTextAtCursor,
  }), [insertMention, insertTextAtCursor]);

  return (
    <>
      <div className="relative w-full">
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className="mx-1.5 mb-1.5 max-h-48 w-full resize-none overflow-y-auto border-none bg-transparent text-base outline-none focus:ring-0"
              spellCheck={true}
            />
          }
          placeholder={<LexicalPlaceholder text={placeholder} />}
          ErrorBoundary={LexicalErrorBoundary}
        />

        <HistoryPlugin />
        <LexicalSendOnEnter onSend={onSend} />
        <LexicalClearPlugin clearTrigger={clearTrigger} />
        <LexicalSendCommand sendTrigger={sendTrigger} onSend={onSend} />
        <MentionsPlugin 
          onOpenContextMenu={onOpenContextMenu}
          onMentionSelect={onMentionSelect} 
        />
        {onTextChange && <OnChangePlugin onChange={handleEditorChange} />}
      </div>
    </>
  );
});

LexicalEditorInner.displayName = "LexicalEditorInner";

// Wrapper component with ref forwarding
interface LexicalEditorWrapperWithRef extends LexicalEditorWrapperProps {
  editorRef?: React.Ref<LexicalEditorHandle>;
}

export const LexicalEditorWrapper: React.FC<LexicalEditorWrapperWithRef> = ({ editorRef, ...props }) => {
  return (
    <LexicalComposer initialConfig={lexicalConfig}>
      <LexicalEditorInner ref={editorRef} {...props} />
    </LexicalComposer>
  );
};